import { validatePcpNameAndContact, type PcpContactMode } from '@/utilities/pcpValidation'
import {
  mapPcpNameAndContactErrors,
  PCP_FORM_ERROR,
  pcpCopyVoice,
  pcpValidationErrorCopy,
} from '@/utilities/pcpCopy'

/** PCP questionnaire + contact fields shared by Patient Portal (flows) and Client Portal. */
export type PcpInfoValues = {
  hasPcp: boolean | null
  needPcpHelp: boolean | null
  pcpName: string
  pcpFax: string
  sendFaxToPcp: boolean
  pcpAddressLine1: string
  pcpAddressLine2: string
  pcpCity: string
  pcpState: string
  pcpZipCode: string
}

export const defaultPcpInfoValues: PcpInfoValues = {
  hasPcp: null,
  needPcpHelp: null,
  pcpName: '',
  pcpFax: '',
  sendFaxToPcp: false,
  pcpAddressLine1: '',
  pcpAddressLine2: '',
  pcpCity: '',
  pcpState: '',
  pcpZipCode: '',
}

export type PcpFieldErrors = {
  hasPcp?: string
  needPcpHelp?: string
  pcpName?: string
  pcpFax?: string
  pcpAddressLine1?: string
  pcpAddressLine2?: string
  pcpCity?: string
  pcpState?: string
  pcpZipCode?: string
}

export type PcpValidateOptions = {
  showNeedPcpHelpQuestion?: boolean
  /** Which contact channel(s) to require. Default `either`. */
  pcpContactMode?: PcpContactMode
  /** Third-person copy for Client Kit Registration. Default false (Patient Portal). */
  isClientKitRegistration?: boolean
}

export function validatePcpForm(
  pcp: PcpInfoValues,
  options: PcpValidateOptions = {},
): PcpFieldErrors {
  const errors: PcpFieldErrors = {}
  const needHelpVisible = options.showNeedPcpHelpQuestion !== false
  const contactMode = options.pcpContactMode ?? 'either'
  const voice = pcpCopyVoice(options.isClientKitRegistration === true)

  if (pcp.hasPcp == null) {
    errors.hasPcp = pcpValidationErrorCopy(PCP_FORM_ERROR.hasPcpRequired, voice)
    return errors
  }

  if (pcp.hasPcp === true) {
    return {
      ...errors,
      ...mapPcpNameAndContactErrors(validatePcpNameAndContact(pcp, { contactMode }), voice),
    }
  }

  if (needHelpVisible && pcp.needPcpHelp == null) {
    errors.needPcpHelp = pcpValidationErrorCopy(PCP_FORM_ERROR.needPcpHelpRequired, voice)
  }

  return errors
}

/** Whether current PCP values would pass validatePcpForm — use to gate submit enablement. */
export function isPcpFormValid(pcp: PcpInfoValues, options: PcpValidateOptions = {}): boolean {
  return Object.keys(validatePcpForm(pcp, options)).length === 0
}
